import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useAuth } from "../../../../firebase/AuthProvider";
import { UpdateHolidayAsync } from "../../../../services/HolidaysService/HolidaysService";
import { HolidayUpsertRequest, HolidayResponse } from "../../../../interfaces";
import { AxiosError } from "axios";

interface EditHolidayModalProps {
  isOpen: boolean;
  onClose: () => void;
  holiday?: HolidayResponse | null;
  onSuccess?: () => void;
}

const getTodayDateString = () => {
  const t = new Date();
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, "0");
  const d = String(t.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const parseLocalDate = (dateStr: string, endOfDay = false) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  if (endOfDay) dt.setHours(23, 59, 59, 999);
  else dt.setHours(0, 0, 0, 0);
  return dt;
};

const formatLocalDateTime = (dateStr: string, endOfDay = false) => {
  const d = new Date(dateStr);
  if (endOfDay) d.setHours(23, 59, 59, 999);
  else d.setHours(0, 0, 0, 0);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  const ms = String(d.getMilliseconds()).padStart(3, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}.${ms}`; // local datetime, no 'Z'
};

const toLocalInputDate = (iso?: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const EditHolidayModal: React.FC<EditHolidayModalProps> = ({ isOpen, onClose, holiday, onSuccess }) => {
  const { currentUser } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (holiday) {
      setTitle(holiday.title || "");
      setDescription(holiday.description || "");
      // convert incoming ISO/local datetimes to yyyy-mm-dd for date inputs
      setStartDate(holiday.startDate ? toLocalInputDate(holiday.startDate) : "");
      setEndDate(holiday.endDate ? toLocalInputDate(holiday.endDate) : "");
      setErrors({});
    } else {
      setTitle("");
      setDescription("");
      setStartDate("");
      setEndDate("");
      setErrors({});
    }
  }, [holiday, isOpen]);

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    if (!title.trim()) e.title = "Title is required";
    if (!startDate) e.startDate = "Start date is required";
    if (!endDate) e.endDate = "End date is required";

    if (startDate) {
      const s = parseLocalDate(startDate);
      if (s < todayStart) {
        e.startDate = "Start date cannot be in the past";
      }
    }

    if (startDate && endDate) {
      const s = parseLocalDate(startDate);
      const en = parseLocalDate(endDate, true);
      if (en < s) {
        e.endDate = "End date must be after or equal to start date";
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev?: React.FormEvent) => {
    ev?.preventDefault();
    if (!holiday) {
      toast.error("No holiday selected for update.");
      return;
    }
    if (!validate()) return;

    const token = await currentUser?.getIdToken();
    if (!token) {
      toast.error("Authentication error — please log in again.");
      return;
    }
    setLoading(true);
    try {
      const payload: HolidayUpsertRequest = {
        title: title.trim(),
        description: description.trim(),
        startDate: formatLocalDateTime(startDate),
        endDate: formatLocalDateTime(endDate, true),
      };

      const res = await UpdateHolidayAsync(token, holiday.id, payload);
      if (res.success) {
        toast.success("Holiday updated successfully!");
        onSuccess?.();
        onClose();
      } else {
        toast.error(res.message || "Failed to update holiday");
      }
    } catch (err: any) {
      toast.error(err instanceof AxiosError ? err.response?.data.message : err?.message || "Error updating holiday");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Holiday</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3 mt-4" controlId="holidayTitle">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter title"
              isInvalid={!!errors.title}
            />
            <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
          </Form.Group>

          {/* <Form.Group className="mb-3" controlId="holidayDescription">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional"
            />
          </Form.Group> */}

          <Form.Group className="mb-3" controlId="holidayStart">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              style={{ paddingLeft: "16px" }}
              min={getTodayDateString()}
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (endDate && parseLocalDate(e.target.value) > parseLocalDate(endDate)) {
                  setEndDate(e.target.value);
                }
              }}
              isInvalid={!!errors.startDate}
            />
            <Form.Control.Feedback type="invalid">{errors.startDate}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" controlId="holidayEnd">
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              style={{ paddingLeft: "16px" }}
              min={startDate || getTodayDateString()}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              isInvalid={!!errors.endDate}
            />
            <Form.Control.Feedback type="invalid">{errors.endDate}</Form.Control.Feedback>
          </Form.Group>

        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "Update Holiday"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditHolidayModal;